import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Card, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import BoltIcon from '@mui/icons-material/BoltOutlined';

import ordersApi from '../../../api/ordersApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import { paymentMethodLabel } from '../../../utils/paymentMethods';
import { ORDER_STATUS_COLORS, orderStatusLabel } from '../../../utils/orderStatuses';
import { SHIPPING_METHOD_LABELS, shippingMethodLabel } from '../../../utils/shippingMethods';
import OrderDetailDialog from '../../../sections/admin/orders/OrderDetailDialog';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency: currency || 'PEN' }).format(
    value || 0
  );

export default function OrdersListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [statuses, setStatuses] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Opcion de entrega (ej. solo "envio_provincia" para ver los pedidos a despachar por agencia).
  const [shippingFilter, setShippingFilter] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { data } = await ordersApi.getOrderStatuses();
        setStatuses(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los estados.'), { variant: 'error' });
      }
    };

    loadStatuses();
  }, [enqueueSnackbar]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ordersApi.getOrders({
        search: search || undefined,
        status: statusFilter || undefined,
        shipping_method: shippingFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de pedidos.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, shippingFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDetailChanged = async () => {
    await fetchOrders();
  };

  const columns = useMemo(
    () => [
      { field: 'client', headerName: 'Cliente', flex: 1, minWidth: 160, valueGetter: (value, row) => row.client?.name || row.customer_name || '—' },
      {
        field: 'email',
        headerName: 'Correo',
        flex: 1,
        minWidth: 200,
        valueGetter: (value, row) => row.client?.email || '—',
      },
      {
        field: 'total',
        headerName: 'Total',
        width: 130,
        valueGetter: (value, row) => formatMoney(row.total, row.currency),
      },
      { field: 'currency', headerName: 'Moneda', width: 90 },
      {
        field: 'payment_method',
        headerName: 'Método de pago',
        width: 140,
        valueFormatter: (value) => paymentMethodLabel(value),
      },
      {
        field: 'shipping_method',
        headerName: 'Entrega',
        width: 190,
        valueGetter: (value, row) =>
          value === 'envio_provincia' && row.shipping_province
            ? `${shippingMethodLabel(value)} · ${row.shipping_province}`
            : shippingMethodLabel(value),
      },
      {
        field: 'status',
        headerName: 'Estado',
        width: 160,
        renderCell: (params) => (
          <Chip
            label={orderStatusLabel(params.value?.name)}
            color={ORDER_STATUS_COLORS[params.value?.name] || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'invoice',
        headerName: 'Comprobante',
        width: 170,
        renderCell: (params) => {
          const invoice = params.value;
          if (!invoice) {
            return (
              <Chip label="Sin emitir" size="small" variant="outlined" />
            );
          }
          // Colores acorde al estado real ante SUNAT (ver OrderDetailDialog): aceptado en
          // verde, con observaciones en ambar, rechazado/error en rojo, anulado/pendiente
          // en gris.
          const color =
            invoice.status === 'aceptada'
              ? 'success'
              : invoice.status === 'observada'
                ? 'warning'
                : ['rechazada', 'error'].includes(invoice.status)
                  ? 'error'
                  : 'default';

          return (
            <Chip
              label={`${invoice.type === 'factura' ? 'Factura' : 'Boleta'} ${invoice.full_number}`}
              color={color}
              size="small"
            />
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Fecha',
        width: 120,
        valueFormatter: (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityIcon />}
            label="Ver detalle"
            onClick={() => setSelectedOrder(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row" spacing={2}>
        <div>
          <Typography variant="h4">Órdenes de venta</Typography>
          <Typography variant="body2" color="text.secondary">
            Compras realizadas por los clientes desde la tienda, con emisión de boleta o factura electrónica (SUNAT).
          </Typography>
        </div>
        <Button variant="contained" startIcon={<BoltIcon />} component={RouterLink} to="/admin/venta-express">
          Venta express
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Buscar"
            placeholder="Nombre o correo del cliente"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Todos"
              color={statusFilter === '' ? 'primary' : 'default'}
              variant={statusFilter === '' ? 'filled' : 'outlined'}
              onClick={() => {
                setStatusFilter('');
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
            {statuses
              .map((status) => (
                <Chip
                  key={status.id}
                  label={orderStatusLabel(status.name)}
                  color={statusFilter === status.id ? ORDER_STATUS_COLORS[status.name] || 'primary' : 'default'}
                  variant={statusFilter === status.id ? 'filled' : 'outlined'}
                  onClick={() => {
                    setStatusFilter(status.id);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                />
              ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Entrega:
            </Typography>
            {[['', 'Todas'], ...Object.entries(SHIPPING_METHOD_LABELS)].map(([value, label]) => (
              <Chip
                key={value || 'todas'}
                label={label}
                color={shippingFilter === value ? 'primary' : 'default'}
                variant={shippingFilter === value ? 'filled' : 'outlined'}
                onClick={() => {
                  setShippingFilter(value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowClick={(params) => setSelectedOrder(params.row)}
        />
      </Card>

      <OrderDetailDialog
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        statuses={statuses}
        onChanged={handleDetailChanged}
      />
    </Stack>
  );
}
